import React, { Fragment, FunctionComponent, useCallback, useEffect } from 'react';
import classnames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { Field, InjectedFormProps } from 'redux-form';
import { useSelector } from 'react-redux';

import { getUserProfile } from 'modules/auth/selectors';
import { useAppDispatch, useReduxFormValue } from 'modules/common/hooks';
import Input from 'modules/common/components/Input';
import FormSubmitButton from 'modules/common/components/FormSubmitButton';
import RolesSelectField from 'modules/admin/components/RolesSelectField';
import { ROLE_TYPE } from 'modules/admin/constants';
import { closeOwnerMembershipDialog } from '../../actions';
import { getOwnerMembershipOperationInProgress } from '../../selectors';

import local from './local.module.scss';

interface OwnerMembershipFormProps {
    canManage: boolean;
}

const UserName = ({ input, meta, disabled }) => {
    const intl = useIntl();

    return (
        <Fragment>
            <div className={local.label}>
                <FormattedMessage id='admin.memberEmail' />
            </div>
            <Input
                inputProperties={input}
                disabled={disabled}
                placeholder={intl.formatMessage({ id: 'admin.usernamePlaceholder' })}
                className={classnames({ 'error-input': Boolean(meta.error) })}
            />
            {
                meta.error &&
                <div className='form-error-message'>
                    <FormattedMessage id={`validationErrors.${meta.error}`} defaultMessage={meta.error} />
                </div>
            }
        </Fragment>
    );
};

const OwnerMembershipForm: FunctionComponent<OwnerMembershipFormProps & InjectedFormProps<{}, OwnerMembershipFormProps>> = ({
    change,
    handleSubmit,
    canManage
}) => {
    const { username: currentUsername } = useSelector(getUserProfile);

    useEffect(() => {
        if (!canManage) {
            change('username', currentUsername);
        }
    }, [ canManage ]);

    const dispatch = useAppDispatch();

    const close = useCallback(() => {
        dispatch(closeOwnerMembershipDialog());
    }, []);

    const username = useReduxFormValue('ownerMembershipForm', 'username');
    const roles = useReduxFormValue('ownerMembershipForm', 'roles');

    const isOperationInProgress = useSelector(getOwnerMembershipOperationInProgress);

    return (
        <Fragment>
            <form id='ownerMembershipForm' onSubmit={handleSubmit} autoComplete='off'>
                <Field
                    name='username'
                    disabled={isOperationInProgress || !canManage}
                    component={UserName}
                />
                <div className={local.label}>
                    <FormattedMessage id='admin.roles' />
                </div>
                <RolesSelectField
                    fieldName='roles'
                    type={ROLE_TYPE.OWNER}
                    width={480}
                    disabled={isOperationInProgress}
                />
            </form>
            <div className={classnames(local.buttonsContainer, 'container-row')}>
                <div className='ls-button'>
                    <button disabled={isOperationInProgress} className='btn-flat' onClick={close}>
                        <FormattedMessage id='common.cancel' />
                    </button>
                </div>
                <FormSubmitButton
                    form='ownerMembershipForm'
                    width={130}
                    disabled={!username || !roles?.length || isOperationInProgress}
                    text={<FormattedMessage id='common.submit' />}
                    isSubmitting={isOperationInProgress}
                />
            </div>
        </Fragment>
    );
};

export default OwnerMembershipForm;
